import { FURNITURE_TYPES, LIGHTING_TYPES, computeRoomAutoWalls } from '../store/houseSlice';
import { edgeLength } from './freeWallRoom';

// ─── PROJECT COST ESTIMATE ───────────────────────────────────────────────────
// Parses a saved scene (project.data) and derives, per room, the floor / wall /
// ceiling surface areas (m²) and the list of placed items — the raw quantities
// the cost calculator multiplies by user-entered unit prices.
//
// Units: room x/y/w/h are grid cells and 1 cell = 1 metre, so areas come out in
// m² directly. Furniture x/y/w/h are in pixels (26 px = 1 cell), so membership
// tests convert with CELL.
const CELL = 26;

const roomRects = (room) =>
  (room.rects && room.rects.length ? room.rects : [{ x: room.x, y: room.y, w: room.w, h: room.h }]);

function floorArea(room) {
  return roomRects(room).reduce((s, r) => s + r.w * r.h, 0); // m²
}

function perimeter(room) {
  // A DRAWN room's walls follow its smooth outline, which computeRoomAutoWalls
  // deliberately excludes (tracing its rasterised cells would return a staircase
  // around every curve) — so it measured ZERO wall for these rooms. Sum the
  // outline edges instead, arc length for the curved ones, which is the wall
  // that actually gets built.
  if (room.pts && room.pts.length >= 3) {
    return room.pts.reduce((s, a, i) => s + edgeLength(a, room.pts[(i + 1) % room.pts.length]), 0);
  }
  try {
    return computeRoomAutoWalls([room]).reduce((s, w) => {
      const len = w.y1 === w.y2 ? Math.abs(w.x2 - w.x1) : Math.abs(w.y2 - w.y1);
      return s + len;
    }, 0);
  } catch {
    return 2 * ((room.w || 0) + (room.h || 0)); // rectangular fallback
  }
}

function itemsInRoom(room, furniture, lighting) {
  const rects = roomRects(room);
  const inside = (cx, cy) => rects.some(r =>
    cx >= r.x * CELL && cx <= (r.x + r.w) * CELL &&
    cy >= r.y * CELL && cy <= (r.y + r.h) * CELL);

  const all = [
    ...furniture.map(f => ({ ...f, _kind: 'furniture' })),
    ...lighting.map(l => ({ ...l, _kind: 'lighting' })),
  ];

  // Group identical items (same kind+type) in the room into one line with a qty.
  const map = new Map();
  for (const it of all) {
    const rot = it.rotation || 0;
    const dw = rot % 180 === 0 ? it.w : it.h;
    const dh = rot % 180 === 0 ? it.h : it.w;
    if (!inside(it.x + dw / 2, it.y + dh / 2)) continue;
    const meta = it._kind === 'lighting'
      ? LIGHTING_TYPES.find(l => l.type === it.type)
      : FURNITURE_TYPES.find(f => f.type === it.type);
    const key = `${it._kind}:${it.type}`;
    if (!map.has(key)) map.set(key, { key, type: it.type, kind: it._kind, label: meta?.label || it.type, count: 0 });
    map.get(key).count += 1;
  }
  return [...map.values()];
}

// round to 2 decimals, trimming trailing zeros
export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

export function estimateProject(data) {
  let scene = {};
  try { scene = typeof data === 'string' ? JSON.parse(data || '{}') : (data || {}); }
  catch { scene = {}; }

  const rooms = scene.rooms || [];
  const furniture = scene.furniture || [];
  const lighting = scene.lighting || [];
  const wallHeight = scene.wallHeight ?? 2.8;

  const roomsOut = rooms.map((room, i) => {
    const fa = round2(floorArea(room));
    const wa = round2(perimeter(room) * wallHeight);
    return {
      id: room.id ?? i,
      label: room.label || room.type || `Room ${i + 1}`,
      floorSqm: fa,
      ceilingSqm: fa,
      wallSqm: wa,
      items: itemsInRoom(room, furniture, lighting).map(it => ({ ...it, roomId: room.id ?? i, roomLabel: room.label || room.type || `Room ${i + 1}` })),
    };
  });

  // Free walls standing on their own — drawn at any angle, possibly curved, and
  // not part of any room's outline, so no room perimeter covers them. Curved
  // ones measure by arc length: that's the wall you build. Their area joins the
  // wall total alongside the rooms' straight walls.
  const freeWallSqm = round2(
    (scene.freeWalls || []).reduce(
      (s, w) => s + edgeLength({ x: w.x1, y: w.y1, bulge: w.bulge || 0 }, { x: w.x2, y: w.y2 }),
      0,
    ) * wallHeight,
  );

  return {
    rooms: roomsOut,
    items: roomsOut.flatMap(r => r.items.map(it => ({ ...it, uid: `${r.id}:${it.key}` }))),
    freeWallSqm,
    totalFloor: round2(roomsOut.reduce((s, r) => s + r.floorSqm, 0)),
    totalWall: round2(roomsOut.reduce((s, r) => s + r.wallSqm, 0) + freeWallSqm),
    totalCeiling: round2(roomsOut.reduce((s, r) => s + r.ceilingSqm, 0)),
  };
}

// Apply a saved breakdown (rates + per-item prices) to a fresh estimate and
// return the grand total. New items (no saved price) count as 0; removed items
// simply drop out. Shared by the calculator's live total and the card reload.
const parseNum = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
export function computeTotalFromBreakdown(est, breakdown = {}) {
  const floorCost = est.totalFloor   * parseNum(breakdown.floorRate);
  const wallCost  = est.totalWall    * parseNum(breakdown.wallRate);
  const ceilCost  = est.totalCeiling * parseNum(breakdown.ceilRate);
  const prices = breakdown.itemPrices || {};
  const itemsCost = est.items.reduce((s, it) => s + it.count * parseNum(prices[it.uid]), 0);
  return round2(floorCost + wallCost + ceilCost + itemsCost);
}

// Recompute a project's total from its stored priceData against its CURRENT
// scene — so a reload picks up rooms/items added or removed since. Returns
// { grand, currency, priceData } or null if there's no saved breakdown to reuse.
export function recalcProject(project) {
  let breakdown = null;
  try { breakdown = project?.priceData ? JSON.parse(project.priceData) : null; } catch { breakdown = null; }
  if (!breakdown) return null;
  const est = estimateProject(project.data);
  const grand = computeTotalFromBreakdown(est, breakdown);
  const currency = breakdown.currency || project.amount || 'USD';
  return { grand, currency, priceData: JSON.stringify({ ...breakdown, grand }) };
}

// Currency options — stored on the project as `amount`, displayed via symbol.
export const CURRENCIES = [
  { code: 'USD', symbol: '$',  label: 'Dollar' },
  { code: 'RUB', symbol: '₽',  label: 'Ruble' },
  { code: 'AMD', symbol: '֏',  label: 'Dram' },
];

export const currencySymbol = (code) => (CURRENCIES.find(c => c.code === code)?.symbol) || '$';
